/* @flow */
import { cloneDeep, once } from 'lodash';
import gql from 'graphql-tag';

import { dispatcher } from 'lib/Dispatcher';
import { touch } from 'actions/touchActions';
import { loadStaffClassForOrgId } from 'groups/lib/GroupPromises';
import CurrentUser from 'auth/CurrentUser';
import GroupActions from 'groups/actions/GroupActions';
import Organization from 'entities/Organization';
import organizationDefaultFragment from 'organizations/fragments/organizationDefaultFragment.gql';
import Organizations from 'collections/Organizations';
import OrganizationStore from 'stores/OrganizationStore';
import RemindAPI from 'lib/RemindAPI';
import ToastActions from 'actions/ToastActions';
import userOrganizationsQuery from 'organizations/queries/UserOrganizationsQuery.gql';
import { writeApolloCacheForChatMessages } from 'chat/actions/ChatMessageActions';
import ChatMessage from 'entities/ChatMessage';

import { ActionTypes } from '../constants/OrganizationConstants';
import { ReportOptions, ReportOtherOption } from 'constants/ReportConstants';

type UserInfo = {
  first_name: string,
  last_name: string,
  address: string,
};

const lazyWatchUserOrganizations = once((): void => {
  function onQueryChange(data: Object) {
    const organizations = data.me.organizations.map(org => Organization.fromJSON(cloneDeep(org)));

    dispatcher.dispatch({
      type: ActionTypes.LOAD_USER_ORGANIZATIONS,
      organizations: Organizations.fromJSON(cloneDeep(organizations)),
    });
  }

  RemindAPI.registerWatchQuery({
    query: userOrganizationsQuery,
    onQueryChange,
  });
});

const OrganizationActions = {
  /**
   * Get the organization from the store. If isn't available then load it from
   * the server.
   */
  getOrLoadOrganization(id: Backbone$Id): Promise<Organization> {
    const preloadedOrg = OrganizationStore.getOrganization(id);
    if (preloadedOrg) {
      return Promise.resolve(preloadedOrg);
    } else {
      return this.loadOrganization(id);
    }
  },

  /**
   * Fetch all the organizations with which the current user is affiliated.
   */
  loadUserOrganizations(forceFetch: boolean = true): Promise<void> {
    lazyWatchUserOrganizations();
    return RemindAPI.execute({ query: userOrganizationsQuery, forceFetch });
  },

  /**
   * Fetch the organization from the server.
   */
  async loadOrganization(id: Backbone$Id, forceFetch: boolean = true): Promise<Organization> {
    const query = gql`
      ${organizationDefaultFragment}
      query LoadOrganization($orgIds: [Int]!) {
        organizations(ids: $orgIds) {
          ...organizationDefault
        }
      }
    `;

    const variables = { orgIds: [id] };
    const data = await RemindAPI.execute({ query, variables, forceFetch });

    const organization = Organization.fromJSON(cloneDeep(data.organizations[0]));

    dispatcher.dispatch({
      type: ActionTypes.LOAD_ORGANIZATION,
      organization,
    });
    // $FlowFixMe this method should be a promise because its's async.  Ignoring
    return organization;
  },

  createOrganization(attr: Object): Promise<Organization> {
    return new Organization(attr).create();
  },

  addOrganization(organization: Organization) {
    touch('NAVIGATION_LIST');
    dispatcher.dispatch({
      type: ActionTypes.LOAD_ORGANIZATION,
      organization,
    });
  },

  removeOrganization(orgId: Backbone$Id): void {
    touch('NAVIGATION_LIST');
    dispatcher.dispatch({
      type: ActionTypes.REMOVE_ORGANIZATION,
      orgId,
    });
  },

  /**
   * Adds a set of users to an organization via the admin's direct add flow
   * @param {array} users an array of JSON objects with first_name, last_name and email
   * @return {Promise} for a job status object that can be pinged for results
   */
  addUsers(users: UserInfo[], role: string, orgId: Backbone$Id): Promise<*> {
    return RemindAPI.request(`/organizations/${orgId}/${role}`, 'POST', {
      users,
    });
  },

  batchRequestTranscript(
    orgId: Backbone$Id,
    transcripts: {
      user_id: Backbone$Id,
      request_from_affected_user: boolean,
    }[],
    note: string,
    inclChat: boolean,
    inclAnnouncements: boolean,
    startDate: Date,
    fromAdmin: boolean
  ) {
    return RemindAPI.request(`/organizations/${orgId}/email_transcript/batch`, 'POST', {
      incl_announcements: inclAnnouncements,
      incl_chat: inclChat,
      transcripts,
      note,
      start_date: startDate,
      from_admin: fromAdmin,
    });
  },

  batchRemoveFromOrg(orgId: Backbone$Id, userIds: Backbone$Id[], reason: string): Promise<{}> {
    return RemindAPI.request(`/organizations/${orgId}/users/batch`, 'DELETE', {
      reason,
      user_ids: userIds,
    });
  },

  batchBanFromOrg(orgId: Backbone$Id, userIds: Backbone$Id[], reason: string): Promise<{}> {
    return RemindAPI.request(`/organizations/${orgId}/users/ban/batch`, 'POST', {
      reason,
      user_ids: userIds,
    });
  },

  reportUserAsAdmin(orgId: Backbone$Id, userId: Backbone$Id, key: string, notes: string) {
    return RemindAPI.request(`/organizations/${orgId}/users/${userId}/report`, 'POST', {
      key,
      reason: ReportOptions[key] || ReportOtherOption,
      notes,
    });
  },

  getDashboardStats(orgId: Backbone$Id) {
    return RemindAPI.request(`/organizations/${orgId}/detailed_statistics/dashboard`, 'GET');
  },

  getActiveSchools(orgId: Backbone$Id, timeWindow: ?string) {
    const query = timeWindow ? `?time_window=${timeWindow}` : '';
    return RemindAPI.request(
      `/organizations/${orgId}/detailed_statistics/active_schools${query}`,
      'GET'
    );
  },

  getActiveTeachers(orgId: Backbone$Id, timeWindow: ?string) {
    const query = timeWindow ? `?time_window=${timeWindow}` : '';
    return RemindAPI.request(
      `/organizations/${orgId}/detailed_statistics/active_teachers${query}`,
      'GET'
    );
  },

  getAnnouncementsStream(orgId: Backbone$Id, limit: number, groupLimit: number) {
    return RemindAPI.request(`/organizations/${orgId}/announcement_stream`, 'GET', {
      limit,
      group_limit: groupLimit,
    }).then(messages => {
      const msgs = messages.map(m => ChatMessage.fromJSON(m));

      try {
        writeApolloCacheForChatMessages(msgs);
      } catch (e) {
        console.error('could not save annoucements stream message reaction into cache: ', e);
      }

      return messages;
    });
  },

  leaveOrg(removingId: number, force: boolean = false) {
    const userId = CurrentUser.getUserId();

    const onRemoveSuccess = async () => {
      touch('NAVIGATION_LIST');
      OrganizationActions.loadUserOrganizations(true);
      const oldStaffClass = await loadStaffClassForOrgId(removingId);
      if (oldStaffClass) {
        GroupActions.removeGroup(oldStaffClass);
      }
    };

    return RemindAPI.request(
      `/organizations/${removingId}/users/${userId}?force=${force ? 'true' : 'false'}`,
      'DELETE'
    ).then(onRemoveSuccess, ToastActions.showDefaultErrorToast);
  },
};

export default OrganizationActions;
